import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { GradientText } from "./GradientText";
import { ArrowRight, Play } from "lucide-react";

/**
 * Hero
 *
 * Hero section with:
 * - Gradient headline treatment
 * - Primary & secondary CTAs
 * - All text from translations
 */
export function Hero() {
  const { t } = useTranslation("landing");

  const headline = t("hero.headline");
  const subheadline = t("hero.subheadline");
  const primaryCTAText = t("hero.primaryCTA");
  const secondaryCTAText = t("hero.secondaryCTA");
  const trustIndicator = t("hero.trustIndicator");

  return (
    <section className="relative flex min-h-[100vh] items-center justify-center overflow-hidden pt-20 pb-16 lg:pt-24 lg:pb-24">
      <div className="container mx-auto px-4 text-center sm:px-6 lg:px-8">
        {/* Trust indicator badge */}
        {trustIndicator && (
          <div className="mb-8">
            <span
              className={cn(
                "inline-flex items-center gap-2 rounded-full px-4 py-2",
                "border border-border bg-muted",
                "text-sm text-muted-foreground",
              )}
            >
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
              {trustIndicator}
            </span>
          </div>
        )}

        {/* Headline */}
        <h1
          className={cn(
            "text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl",
            "tracking-display leading-[1.1] font-semibold",
            "mx-auto mb-6 max-w-5xl lg:mb-8",
          )}
        >
          <GradientText>{headline}</GradientText>
        </h1>

        {/* Subheadline */}
        <p
          className={cn(
            "text-base sm:text-lg lg:text-xl",
            "leading-relaxed text-muted-foreground",
            "mx-auto mb-8 max-w-2xl lg:mb-10",
          )}
        >
          {subheadline}
        </p>

        {/* CTAs */}
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link to="/signup">
            <Button size="lg" className={cn("h-12 px-8 text-base", "transition-all duration-300")}>
              {primaryCTAText}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>

          {secondaryCTAText && (
            <Button
              variant="ghost"
              size="lg"
              className={cn(
                "h-12 px-8 text-base",
                "text-foreground hover:text-foreground",
                "hover:bg-muted",
                "transition-all duration-300",
              )}
            >
              <Play className="mr-2 h-4 w-4" />
              {secondaryCTAText}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
